/**
 * Sync Status Badge Component
 * Shows current connection status with visual indicators
 */

import React from 'react';
import { Box, Chip, Tooltip } from '@mui/material';
import {
    CloudDone,
    CloudOff,
    CloudSync,
    Error as ErrorIcon,
    WifiOff
} from '@mui/icons-material';
import { SyncStatus } from '../../services/yjs';

const statusConfig = {
    [SyncStatus.SYNCED]: {
        label: 'Synced',
        color: 'success',
        icon: CloudDone,
        tooltip: 'All changes synced to server'
    },
    [SyncStatus.SYNCING]: {
        label: 'Syncing',
        color: 'warning',
        icon: CloudSync,
        tooltip: 'Syncing changes...'
    },
    [SyncStatus.CONNECTED]: {
        label: 'Connected',
        color: 'info',
        icon: CloudSync,
        tooltip: 'Connected to server'
    },
    [SyncStatus.CONNECTING]: {
        label: 'Connecting',
        color: 'default',
        icon: CloudSync,
        tooltip: 'Connecting to server...'
    },
    [SyncStatus.OFFLINE]: {
        label: 'Offline',
        color: 'warning',
        icon: WifiOff,
        tooltip: 'Offline - saving locally'
    },
    [SyncStatus.DISCONNECTED]: {
        label: 'Disconnected',
        color: 'default',
        icon: CloudOff,
        tooltip: 'Disconnected from server'
    },
    [SyncStatus.ERROR]: {
        label: 'Error',
        color: 'error',
        icon: ErrorIcon,
        tooltip: 'Sync error - check connection'
    }
};

export function SyncStatusBadge({ status, pendingChanges, lastSyncTime, ...props }) {
    const config = statusConfig[status] || statusConfig[SyncStatus.DISCONNECTED];
    const Icon = config.icon;
    
    // Calculate last sync text
    let lastSyncText = '';
    if (lastSyncTime) {
        const secondsAgo = Math.floor((Date.now() - lastSyncTime) / 1000);
        if (secondsAgo < 60) {
            lastSyncText = `${secondsAgo}s ago`;
        } else if (secondsAgo < 3600) {
            lastSyncText = `${Math.floor(secondsAgo / 60)}m ago`;
        } else {
            lastSyncText = `${Math.floor(secondsAgo / 3600)}h ago`;
        }
    }
    
    // Build tooltip text
    let tooltipText = config.tooltip;
    if (pendingChanges && status === SyncStatus.OFFLINE) {
        tooltipText = '📤 Changes saved locally - will sync when online';
    } else if (lastSyncText && status === SyncStatus.SYNCED) {
        tooltipText = `Last synced: ${lastSyncText}`;
    }
    
    return (
        <Tooltip title={tooltipText} arrow>
            <Chip
                icon={<Icon />}
                label={config.label}
                color={config.color}
                size="small"
                variant={status === SyncStatus.SYNCED ? "filled" : "outlined"}
                {...props}
            />
        </Tooltip>
    );
}

export default SyncStatusBadge;
